import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Select from '../../../components/ui/Select';

const AudioSettingsSection = ({ audioSettings, onUpdate }) => {
  const [settings, setSettings] = useState({
    microphone: audioSettings?.microphone || 'default',
    quality: audioSettings?.quality || 'high',
    format: audioSettings?.format || 'mp3',
    bitrate: audioSettings?.bitrate || '320',
    sampleRate: audioSettings?.sampleRate || '48000'
  });
  const [isTesting, setIsTesting] = useState(false);
  const [testLevel, setTestLevel] = useState(0);

  const microphoneOptions = [
    { value: 'default', label: 'Микрофон по умолчанию' },
    { value: 'built-in', label: 'Встроенный микрофон' },
    { value: 'usb-mic', label: 'USB микрофон' },
    { value: 'audio-interface', label: 'Аудиоинтерфейс' }
  ];

  const qualityOptions = [
    { value: 'low', label: 'Низкое (64 kbps)', description: 'Для медленного интернета' },
    { value: 'medium', label: 'Среднее (128 kbps)', description: 'Баланс качества и размера' },
    { value: 'high', label: 'Высокое (320 kbps)', description: 'Студийное качество' }
  ];

  const formatOptions = [
    { value: 'mp3', label: 'MP3', description: 'Универсальный формат' },
    { value: 'aac', label: 'AAC', description: 'Лучшее сжатие' },
    { value: 'wav', label: 'WAV', description: 'Без потерь' },
    { value: 'flac', label: 'FLAC', description: 'Lossless сжатие' }
  ];

  const bitrateOptions = [
    { value: '64', label: '64 kbps' },
    { value: '128', label: '128 kbps' },
    { value: '192', label: '192 kbps' },
    { value: '256', label: '256 kbps' },
    { value: '320', label: '320 kbps' }
  ];

  const sampleRateOptions = [
    { value: '44100', label: '44.1 kHz', description: 'CD качество' },
    { value: '48000', label: '48 kHz', description: 'Профессиональное' },
    { value: '96000', label: '96 kHz', description: 'Hi-Res аудио' }
  ];

  const handleSettingChange = (name, value) => {
    setSettings(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleTestMicrophone = () => {
    setIsTesting(true);
    let level = 0;
    const interval = setInterval(() => {
      level = Math.random() * 100;
      setTestLevel(level);
    }, 100);

    setTimeout(() => {
      clearInterval(interval);
      setIsTesting(false);
      setTestLevel(0);
    }, 3000);
  };

  const handleSave = () => {
    onUpdate(settings);
  };

  return (
    <div className="audio-settings-section bg-card rounded-lg p-6 shadow-soft">
      <div className="section-header flex items-center gap-3 mb-6">
        <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center">
          <Icon name="Mic" size={20} color="var(--color-primary)" />
        </div>
        <div>
          <h2 className="text-xl font-heading font-semibold text-foreground">
            Настройки аудио
          </h2>
          <p className="text-sm text-muted-foreground">
            Конфигурация микрофона и качества записи
          </p>
        </div>
      </div>
      <div className="settings-content space-y-6">
        <div className="microphone-section">
          <Select
            label="Устройство ввода"
            description="Выберите микрофон для записи"
            options={microphoneOptions}
            value={settings?.microphone}
            onChange={(value) => handleSettingChange('microphone', value)}
          />
          
          <div className="microphone-test mt-4 p-4 bg-muted/50 rounded-lg">
            <div className="flex items-center justify-between mb-3">
              <span className="text-sm font-medium text-foreground">
                Тест микрофона
              </span>
              <Button
                variant="outline"
                size="sm"
                iconName={isTesting ? 'Square' : 'Play'}
                iconPosition="left"
                onClick={handleTestMicrophone}
                disabled={isTesting}
              >
                {isTesting ? 'Тестирование...' : 'Начать тест'}
              </Button>
            </div>
            <div className="test-level-bar h-2 bg-background rounded-full overflow-hidden">
              <div
                className="h-full bg-primary transition-all duration-100"
                style={{ width: `${testLevel}%` }}
              />
            </div>
          </div>
        </div>

        <Select
          label="Качество записи"
          description="Выберите качество аудио"
          options={qualityOptions}
          value={settings?.quality}
          onChange={(value) => handleSettingChange('quality', value)}
        />

        <Select
          label="Формат файла"
          description="Формат сохранения записей"
          options={formatOptions}
          value={settings?.format}
          onChange={(value) => handleSettingChange('format', value)}
        />

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Select
            label="Битрейт"
            options={bitrateOptions}
            value={settings?.bitrate}
            onChange={(value) => handleSettingChange('bitrate', value)}
          />

          <Select
            label="Частота дискретизации"
            options={sampleRateOptions}
            value={settings?.sampleRate}
            onChange={(value) => handleSettingChange('sampleRate', value)}
          />
        </div>

        <div className="audio-info p-4 bg-accent/10 rounded-lg border border-accent/20">
          <div className="flex items-start gap-3">
            <Icon name="Info" size={20} color="var(--color-accent)" />
            <div>
              <h4 className="text-sm font-medium text-foreground mb-1">
                Рекомендации по настройке
              </h4>
              <p className="text-xs text-muted-foreground">
                Для оптимального качества используйте настройки: 320 kbps, 48 kHz, формат AAC или FLAC. Убедитесь, что микрофон находится на расстоянии 15-20 см от рта.
              </p>
            </div>
          </div>
        </div>

        <div className="form-actions flex items-center gap-3 pt-6 border-t border-border">
          <Button
            variant="default"
            onClick={handleSave}
            iconName="Check"
            iconPosition="left"
          >
            Сохранить настройки
          </Button>
        </div>
      </div>
    </div>
  );
};

export default AudioSettingsSection;